import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ODataFilterBuilder } from 'odata-filter-builder';
import { ClientsService } from './clients.service';
import { FiltersService } from './filters-service';
import { GroupsService } from './groups.service';
import { LocalesService } from './locales.service';
import { ProfileService } from './profile.service';
import { StudentsService } from './students.service';
import {
  DropdownQuestion,
  EntityQuestion,
  QuestionBase,
  TagsQuestion,
  CheckboxQuestion,
  HiddenQuestion,
  DatetimeQuestion,
  TextboxQuestion
} from 'core/components/forms';
import { CountriesService } from 'core/services/countries.service';
import { AgentsService } from 'core/services/agents.service';
import { DistributorsService } from 'core/services/distributors.service';
import { CoursesService } from './courses.service';
import { DateTime } from 'luxon';

@Injectable()
export class CoreFiltersService implements FiltersService {
  public filters: any;
  public filtersQuestions: QuestionBase<any>[];
  userRole: any;
  // Filter name needs to be the same as its question's key in order to work via
  // ListFiltersComponent.applyFilters() > this.buildFilterString() for fetch
  public filterNamesMap = {
    distributor: (params) => this.setDistributorFilter(params), // TODO Remove if unused
    distributorId: (params) => this.setDistributorFilter(params),
    client: (params) => this.setClientFilter(params),
    clientId: (params) => this.setClientFilter(params),
    clientDistributor: (params) =>
      this.setClientFilter({
        ...params,
        filter: {
          key: 'clientDistributor',
          fieldName: 'distributorId',
          searchField: '_id',
          filterLabel: 'filters.clientDistributor',
          ...params.filter,
          options: { entityOptions: { valueField: 'distributorId', filters: { $filter: 'distributorId ne null' } } }
        }
      }),
    game: (params) => this.setGameFilter(params), // TODO Remove if unused
    gameId: (params) => this.setGameFilter(params),
    group: (params) => this.setGroupFilter(params),
    groupId: (params) => this.setGroupFilter(params), // TODO Remove if unused
    role: (params) => this.setRoleFilter(params),
    agent: (params) => this.setAgentFilter(params),
    agentIds: (params) => this.setAgentFilter(params),
    user: (params) => this.setUserFilter(params), // TODO Remove if unused
    userId: (params) => this.setUserFilter(params),
    country: (params) => this.setCountryFilter(params),
    countryId: (params) => this.setCountryFilter(params, 'countryId'),
    lang: (params) => this.setLanguageFilter(params),
    language: (params) => this.setLanguageFilter(params), // TODO Remove if unused
    timePeriod: (params) => this.getTimePeriodFilter(params),
    created_at: (params) => this.setDateFilters(params, 'created_at'),
    startDate: (params) => this.setDateFilters(params, 'startDate'),
    endDate: (params) => this.setDateFilters(params, 'endDate'),
    currentDate: (params) => this.setCurrentDateFilter(params),
    licenseStatus: (params) => this.setLicenseStatusFilter(params),
    hasBonus: (params) => this.setFlagFilter(params, 'hasBonus'),
    ssoEnabled: (params) => this.setFlagFilter(params, 'ssoEnabled'),
    tags: (params) => this.setTagsFilter(params),
    active: (params) => this.setActiveFilter(params),
    studentStatus: (params) => this.getStudentStatusFilter(params),
    default: (params, filterName) => this.getDefaultFilter(params, filterName)
  };

  constructor(
    public distributors: DistributorsService,
    public clients: ClientsService,
    public groups: GroupsService,
    public agents: AgentsService,
    public students: StudentsService,
    public profileService: ProfileService,
    public coursesService: CoursesService,
    public countries: CountriesService,
    public locales: LocalesService,
    public translateService: TranslateService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  public createFilters(params: any = {}): any {
    const filters = {};

    if (params.scheme) {
      params.scheme.forEach((filter) => {
        if (filter) {
          const filterName = typeof filter === 'string' ? filter : filter.name;
          if (filterName) {
            const filterParams = typeof filter === 'string' ? params : { ...params, filter };
            const filterObject = this.filterNamesMap[filterName]
              ? this.filterNamesMap[filterName](filterParams)
              : this.filterNamesMap.default(filterParams, filterName);
            if (filterObject) {
              filters[filterName] = filterObject;
            }
          }
        }
      });
    }
    return filters;
  }

  private getDefaultFilter(params: any, filterName: string) {
    return {
      question: new TextboxQuestion({
        key: filterName,
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value ? filterBuilder.contains(filterName, value) : null
    };
  }

  private setDistributorFilter(params: any) {
    if (['distributorManager', 'csAdmin', 'csm', 'supportSpecialist', 'superAdmin'].includes(this.userRole.name)) {
      return {
        question: new EntityQuestion({
          key: 'distributorId',
          label: this.translateService.instant('nouns.distributor'),
          class: 'col-1',
          entityOptions: { valueField: 'id' },
          value: null,
          dataProvider: this.distributors,
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
          value ? filterBuilder.eq('distributorId', value) : null
      };
    }
  }

  private setClientFilter(params: any) {
    const { client, filter = {} } = params;
    const {
      key = 'clientId',
      fieldName = 'id',
      searchField = 'clientId',
      filterLabel = 'nouns.client',
      options = {}
    } = filter;
    if (this.userRole.scope !== 'client') {
      return {
        question: new EntityQuestion({
          key,
          label: this.translateService.instant(filterLabel),
          class: 'col-1',
          value: client ? client[fieldName] : null,
          entity: client,
          entityOptions: { valueField: fieldName },
          dataProvider: this.clients,
          eventHandlers: {
            onSelected: (clientSelected) => this.createFilters(Object.assign(params, { client: clientSelected }))
          },
          ...params.filter,
          ...options
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) => {
          return value ? filterBuilder.eq(searchField, value) : null;
        }
      };
    } else {
      return {
        question: new HiddenQuestion({
          key: 'clientId',
          value: this.profileService.getFixedClientId()
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
          value ? filterBuilder.eq(searchField, value) : null
      };
    }
  }

  private setGameFilter(params: any) {
    const gamesOptions = this.coursesService.getGamesOptions();
    let isItinerary = false;
    gamesOptions.unshift({
      section: this.translateService.instant('properties.any'),
      options: [{ key: 'any', value: this.translateService.instant('properties.any') }]
    });
    return {
      question: new DropdownQuestion({
        key: 'gameId',
        label: this.translateService.instant('nouns.game'),
        class: 'col-2',
        options: gamesOptions,
        value: 'any',
        eventHandlers: {
          onSelected: async (selection) => {
            isItinerary = selection.type == 'itinerary';
          }
        },
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) => {
        const [gameId = '', flavor = 'default'] = value && value !== 'any' ? value.split('|') : [];
        if (gameId) {
          filterBuilder.eq(isItinerary ? 'itineraryId' : 'gameId', gameId);
        }
        if (flavor && flavor !== 'default') {
          filterBuilder.eq('gameFlavor', flavor);
        }
      }
    };
  }

  private setGroupFilter(params: any) {
    const { client, group } = params;
    return {
      question: new EntityQuestion({
        key: 'groupId',
        label: this.translateService.instant('nouns.group'),
        class: 'col-2',
        value: group ? group.id : null,
        entity: group,
        entityOptions: {
          preload: !!client,
          multiple: true,
          filters: client ? { clientId: client.id } : null
        },
        dataProvider: this.groups,
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value
          ? value instanceof Array
            ? filterBuilder.in('groupId', value)
            : filterBuilder.eq('groupId', value)
          : null
    };
  }

  private setAgentFilter(params: any) {
    if (['global', 'root'].includes(this.userRole.scope)) {
      return {
        question: new EntityQuestion({
          key: 'agentIds',
          label: this.translateService.instant('nouns.agent'),
          class: 'col-1',
          entityOptions: {
            nameField: 'email',
            listParser: (listAgent) =>
              listAgent
                ? `${listAgent.name || ''} ${listAgent.surname || ''} ${listAgent.email} (${listAgent.role})`
                : ''
          },
          dataProvider: this.agents,
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
          value ? filterBuilder.eq('agentIds', value) : null
      };
    }
  }

  private setUserFilter(params: any) {
    const { client, user } = params;
    return {
      question: new EntityQuestion({
        key: 'userId',
        label: this.translateService.instant('nouns.username'),
        class: 'col-2',
        value: user ? user.id : null,
        entity: user,
        entityOptions: {
          nameField: 'email',
          filters: client ? { clientId: client.id } : null,
          listParser: (listUser) =>
            listUser ? `${listUser.name || ''} ${listUser.surname || ''} ${listUser.email}` : ''
        },
        dataProvider: this.students,
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) => (value ? filterBuilder.eq('userId', value) : null)
    };
  }

  private setRoleFilter(params?: any) {
    const rolesOptions = this.profileService.getRolesOptions(true);
    rolesOptions.unshift({ key: 'any', value: this.translateService.instant('properties.any') });
    return {
      question: new DropdownQuestion({
        key: 'role',
        label: this.translateService.instant('nouns.role'),
        class: 'col-2-offset',
        options: rolesOptions,
        value: 'any',
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value && value !== 'any' ? filterBuilder.eq('role', value) : null
    };
  }

  private setCountryFilter(params?: any, fieldName: string = 'country') {
    const countryOptions: Array<any> = this.countries.getOptions();
    countryOptions.unshift({
      section: this.translateService.instant('properties.default'),
      options: [{ key: 'any', value: this.translateService.instant('properties.any') }]
    });

    return {
      question: new DropdownQuestion({
        key: fieldName,
        label: this.translateService.instant('nouns.country'),
        class: 'col-2-offset',
        options: countryOptions,
        value: 'any',
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value && value !== 'any' ? filterBuilder.eq(fieldName, value) : null
    };
  }

  private setLanguageFilter(params?: any) {
    const localesOptions = this.locales.getDefaultOptions();
    localesOptions.unshift({ key: 'any', value: this.translateService.instant('properties.any') });
    return {
      question: new DropdownQuestion({
        key: 'lang',
        label:
          params && params['filterName']
            ? this.translateService.instant('nouns.language')
            : this.translateService.instant('notifications.language'),
        class: 'col-2-offset',
        options: localesOptions,
        value: 'any',
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value && value !== 'any' ? filterBuilder.eq('lang', value) : null
    };
  }

  private setLicenseStatusFilter(params?: any) {
    const licenseStatuses = this.translateService.instant('licenses.state');
    const statusOptions = Object.keys(licenseStatuses).map((key) => ({
      key,
      value: licenseStatuses[key]
    }));
    return {
      question: new DropdownQuestion({
        key: 'licenseStatus',
        label: this.translateService.instant('nouns.status'),
        class: 'col-2-offset',
        options: statusOptions,
        multiple: true,
        placeholder: this.translateService.instant('actions.selectStatus'),
        value: null,
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) => (value ? filterBuilder.in('status', value) : null)
    };
  }

  private setTagsFilter(params?: any) {
    return {
      question: new TagsQuestion({
        key: 'tags',
        label: this.translateService.instant('nouns.tags'),
        class: 'col-1',
        value: [],
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value ? value.forEach((v) => filterBuilder.contains('tags', v)) : null
      // value ? value.forEach((v) => filterBuilder.eq('tags', v)) : null
    };
  }

  private setActiveFilter(params?: any) {
    return {
      question: new CheckboxQuestion({
        key: 'active',
        label: this.translateService.instant('nouns.active'),
        class: 'col-1',
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) => (value ? filterBuilder.eq('active', value) : null)
    };
  }

  public getTimePeriodFilter(params: any = {}) {
    const filter = params.filter || {};
    const options = filter.options || {};
    const {
      key = 'timePeriod',
      label = this.translateService.instant('filters.timePeriod'),
      value = 'any',
      fieldNames = { startDate: 'startDate', endDate: 'endDate' }
    } = options;

    if (key) {
      const periodOptions = [
        { key: 'any', value: this.translateService.instant('properties.any') },
        { key: 'closed', value: this.translateService.instant('dashboard.closed') },
        { key: 'open', value: this.translateService.instant('dashboard.open') },
        { key: 'pending', value: this.translateService.instant('dashboard.pending') }
      ];
      return {
        question: new DropdownQuestion({
          key,
          label,
          value,
          options: periodOptions,
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, val?: any) => {
          const today = new Date().toISOString();
          const activeGroups = (fb) => fb.ge(fieldNames.endDate, today);
          const openGroups = (fb) => fb.eq(fieldNames.endDate, null);
          const activeOrOpenGroups = (fb) => fb.or(activeGroups).or(openGroups);
          const factory = {
            closed: () => filterBuilder.lt(fieldNames.endDate, today),
            open: () => filterBuilder.le(fieldNames.startDate, today).and(activeOrOpenGroups),
            pending: () => filterBuilder.gt(fieldNames.startDate, today)
          };
          return factory[val] ? factory[val]() : null;
        }
      };
    }
  }

  private getStudentStatusFilter(params: any = {}) {
    const filter = params.filter || {};
    const options = filter.options || {};
    const {
      key = 'studentStatus',
      label = this.translateService.instant('nouns.status'),
      // value = 'any',
      fieldNames = {
        anonymizedAt: 'anonymizedAt',
        suspended: 'suspended',
        active: 'active',
        hasPlayed: 'hasPlayed'
      }
    } = options;

    if (key) {
      const periodOptions = [
        // { key: 'any', value: this.translateService.instant('properties.any') },
        { key: 'active', value: this.translateService.instant('students.status.activated') },
        { key: 'anonymizedAt', value: this.translateService.instant('students.status.anonymized') },
        { key: 'blocked', value: this.translateService.instant('students.status.blocked') },
        { key: 'inTraining', value: this.translateService.instant('students.status.inTraining') },
        { key: 'pending', value: this.translateService.instant('students.status.pending') }
      ];
      return {
        question: new DropdownQuestion({
          key,
          label,
          // value,
          multiple: true,
          options: periodOptions,
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, val?: any) => {
          const today = new Date().toISOString();
          const factory = {
            anonymizedAt: (statusFilterBuilder) => statusFilterBuilder.le(fieldNames.anonymizedAt, today),
            active: (statusFilterBuilder) =>
              statusFilterBuilder
                .and((fb) => fb.eq(fieldNames.suspended, false))
                .and((fb) => fb.eq(fieldNames.active, true))
                .and((fb) => fb.eq(fieldNames.hasPlayed, false))
                .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
            blocked: (statusFilterBuilder) =>
              statusFilterBuilder
                .and((fb) => fb.eq(fieldNames.suspended, true))
                .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
            pending: (statusFilterBuilder) =>
              statusFilterBuilder
                .and((fb) => fb.eq(fieldNames.suspended, false))
                .and((fb) => fb.eq(fieldNames.active, false))
                .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
            inTraining: (statusFilterBuilder) =>
              statusFilterBuilder
                .and((fb) => fb.eq(fieldNames.suspended, false))
                .and((fb) => fb.eq(fieldNames.active, true))
                .and((fb) => fb.eq(fieldNames.hasPlayed, true))
                .and((fb) => fb.eq(fieldNames.anonymizedAt, null))
          };
          if (val && val.length) {
            const sfb = ODataFilterBuilder();
            const filters = val && val.length ? val.map((v) => factory[v](ODataFilterBuilder())) : [];
            filters.forEach((f) => sfb.or(f));
            return filterBuilder.and(sfb);
          }
          return null;
        }
      };
    }
  }

  private setDateFilters(params?: any, fieldName?: string) {
    if (fieldName) {
      return {
        question: new DatetimeQuestion({
          key: fieldName,
          label: this.translateService.instant(`filters.${fieldName}`),
          timezone: 'utc',
          mode: 'range',
          validators: { invalidDate: true },
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
          value ? filterBuilder.ge(fieldName, value[0]).le(fieldName, value[1]) : null
      };
    }
  }

  private setCurrentDateFilter(params?: any) {
    return {
      question: new DatetimeQuestion({
        key: 'currentDate',
        label: this.translateService.instant('filters.currentDate'),
        value: new Date(),
        timezone: 'utc',
        validators: { invalidDate: true },
        ...params.filter
      }),
      condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
        value ? filterBuilder.le('startDate', value).ge('endDate', value) : null
    };
  }

  private setFlagFilter(params?: any, fieldName?: string) {
    if (fieldName) {
      const flagOptions = [
        { key: 'any', value: this.translateService.instant('properties.any') },
        { key: 'true', value: this.translateService.instant('nouns.yes') },
        { key: 'false', value: this.translateService.instant('nouns.no') }
      ];
      return {
        question: new DropdownQuestion({
          key: fieldName,
          label: this.translateService.instant(`filters.${fieldName}`),
          options: flagOptions,
          value: 'any',
          ...params.filter
        }),
        condition: (filterBuilder: ODataFilterBuilder, value?: any) =>
          value && value !== 'any' ? filterBuilder.eq(fieldName, value) : null
      };
    }
  }

  public buildFilterString(values, filters): string {
    const newValues = this.offsetDates(values);
    const $filter = ODataFilterBuilder();
    Object.keys(filters).forEach((key) => filters[key].condition($filter, newValues[key]));

    return $filter.toString();
  }

  private offsetDates(values) {
    const newValues = Object.assign({}, values);
    return Object.assign(newValues, {
      from_startDate: values.from_startDate
        ? DateTime.fromJSDate(values.from_startDate).plus({ hour: -12 }).toJSDate()
        : '',
      from_endDate: values.from_endDate ? DateTime.fromJSDate(values.from_endDate).plus({ hour: -12 }).toJSDate() : '',
      to_startDate: values.to_startDate ? DateTime.fromJSDate(values.to_startDate).plus({ hour: -12 }).toJSDate() : '',
      to_endDate: values.to_endDate ? DateTime.fromJSDate(values.to_endDate).plus({ hour: -12 }).toJSDate() : ''
    });
  }
}
